import FinancialMetricTile from "@/components/company/financial-metric-tile";
import { useTranslation } from "@/hooks/useTranslation";
import {
  type FinancialRatio,
  type FinancialRatioKey,
  latestFinancialRatios,
} from "@/lib/company-financial-ratios";
import { formatCurrency } from "@/lib/format-amount";
import type { CompanyDTO } from "@shaarp/common";
import {
  CalendarArrowDownIcon,
  CalendarArrowUpIcon,
  ChartLineIcon,
  CreditCardIcon,
  HourglassIcon,
  LandmarkIcon,
  PercentIcon,
  PiggyBankIcon,
  ShieldCheckIcon,
} from "lucide-react";
import { useMemo } from "react";

const RATIO_ICONS: Record<
  FinancialRatioKey,
  React.ComponentType<{ className?: string }>
> = {
  cash: PiggyBankIcon,
  equity: LandmarkIcon,
  debt: CreditCardIcon,
  financialAutonomy: ShieldCheckIcon,
  ebitdaMarginRate: PercentIcon,
  returnOnEquity: ChartLineIcon,
  workingCapitalDaysRevenue: HourglassIcon,
  clientPaymentDays: CalendarArrowDownIcon,
  supplierPaymentDays: CalendarArrowUpIcon,
};

/**
 * The ratios of the company's last filed accounts (Pappers): cash, solidity,
 * margins and payment terms, one tile each like the financial summary. Bare
 * tiles: the caller lays them in a `FinancialMetricGrid`.
 */
export default function CardFinancialRatios({
  company,
}: {
  readonly company: CompanyDTO;
}) {
  const { t, currentLanguage } = useTranslation();
  const latest = useMemo(
    () => latestFinancialRatios(company.business?.finances),
    [company.business?.finances],
  );

  if (!latest) return null;

  const formatValue = (ratio: FinancialRatio): string => {
    switch (ratio.unit) {
      case "amount":
        return formatCurrency(ratio.value, "EUR", currentLanguage, {
          notation: "compact",
          maximumFractionDigits: 1,
        });
      case "percent":
        return new Intl.NumberFormat(currentLanguage, {
          style: "percent",
          maximumFractionDigits: 1,
        }).format(ratio.value / 100);
      case "days":
        return t("company.financialRatios.days", {
          count: Math.round(ratio.value),
        });
    }
  };

  return (
    <>
      {latest.ratios.map((ratio) => (
        <FinancialMetricTile
          key={ratio.key}
          icon={RATIO_ICONS[ratio.key]}
          label={t(`company.financialRatios.items.${ratio.key}`)}
          year={latest.year}
          value={formatValue(ratio)}
          hint={
            ratio.key === "workingCapitalDaysRevenue"
              ? t("company.financialRatios.workingCapitalHint")
              : undefined
          }
        />
      ))}
    </>
  );
}
